import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import stripeApiService from '../../../services/stripe-subscription.service';
import { IconLock, IconCreditCard, IconMapPin, IconX } from '@tabler/icons-react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import './UpdatePaymentMethod.css';

const UpdatePaymentMethodModal = ({
  isOpen,
  onClose,
  stripeCustomerId,
  onPaymentMethodChanged,
  organizationData,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  // Form state
  const [cardholderName, setCardholderName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('US');
  const [setAsDefault, setSetAsDefault] = useState(true);

  // Validation & processing state
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Stripe element styles
  const stripeElementStyle = {
    base: {
      color: '#1f2937',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '15px',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  };

  const stripeElementOptions = {
    style: stripeElementStyle,
    showIcon: true,
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setCardholderName('');
    setStreetAddress('');
    setCity('');
    setState('');
    setZipCode('');
    setCountry('US');
    setSetAsDefault(true);
    setError(null);
    setFieldErrors({});
    setCardNumberComplete(false);
    setCardExpiryComplete(false);
    setCardCvcComplete(false);
  };

  // Card element change handlers
  const handleCardNumberChange = (event) => {
    setCardNumberComplete(event.complete);
    if (event.error) {
      setFieldErrors((prev) => ({ ...prev, cardNumber: event.error.message }));
    } else {
      setFieldErrors((prev) => {
        const { cardNumber, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCardExpiryChange = (event) => {
    setCardExpiryComplete(event.complete);
    if (event.error) {
      setFieldErrors((prev) => ({ ...prev, cardExpiry: event.error.message }));
    } else {
      setFieldErrors((prev) => {
        const { cardExpiry, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCardCvcChange = (event) => {
    setCardCvcComplete(event.complete);
    if (event.error) {
      setFieldErrors((prev) => ({ ...prev, cardCvc: event.error.message }));
    } else {
      setFieldErrors((prev) => {
        const { cardCvc, ...rest } = prev;
        return rest;
      });
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!cardholderName.trim()) {
      errors.cardholderName = 'Cardholder name is required';
    }

    if (!streetAddress.trim()) {
      errors.streetAddress = 'Street address is required';
    }

    if (!city.trim()) {
      errors.city = 'City is required';
    }

    if (!state) {
      errors.state = 'State is required';
    }

    if (!zipCode.trim()) {
      errors.zipCode = 'ZIP code is required';
    }

    if (!cardNumberComplete) {
      errors.cardNumber = 'Please enter a valid card number';
    }

    if (!cardExpiryComplete) {
      errors.cardExpiry = 'Please enter a valid expiry date';
    }

    if (!cardCvcComplete) {
      errors.cardCvc = 'Please enter a valid CVV';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);

      if (!cardNumberElement) {
        setError('Card information is required');
        setProcessing(false);
        return;
      }

      // Create payment method with Stripe
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          name: cardholderName,
          address: {
            line1: streetAddress,
            city: city,
            state: state,
            postal_code: zipCode,
            country: country,
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      // Send to backend to attach to customer and optionally set as default
      await stripeApiService.updateSubscriptionPaymentMethod(
        stripeCustomerId,
        paymentMethod.id,
        false, // isExisting = false for new cards
      );

      toast.success('Payment method added successfully!');
      onPaymentMethodChanged(false);
      onClose(true);
      resetForm();
    } catch (err) {
      console.error('Payment method error:', err);
      toast.error('Failed to add payment method');
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // US States list
  const US_STATES = [
    { value: '', label: 'Select' },
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
  ];

  return (
    <>
      <Modal.Header className="update-payment-modal-header">
        <div>
          <Modal.Title as="h3">Add Payment Method</Modal.Title>
          <p className="modal-subtitle">
            Enter your new payment information securely
          </p>
        </div>
        <button
          className="modal-close-btn"
          onClick={onClose}
          disabled={processing}
        >
          <IconX size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className="update-payment-modal-body">
        <form onSubmit={handleSubmit} id="payment-form">
          {/* Secure Payment Notice */}
          <div className="secure-payment-notice">
            <IconLock size={20} className="lock-icon" />
            <div>
              <h4>Secure Payment Processing</h4>
              <p>
                Your payment information is encrypted and securely processed. We
                never store your full card details.
              </p>
            </div>
          </div>

          {/* Card Information Section */}
          <div className="form-section">
            <div className="section-header">
              <IconCreditCard size={18} />
              <h4>Card Information</h4>
            </div>

            {/* Card Number */}
            <div className="form-field">
              <label htmlFor="cardNumber">
                Card Number <span className="required">*</span>
              </label>
              <div
                className={`stripe-element-wrapper ${fieldErrors.cardNumber ? 'error' : ''}`}
              >
                <CardNumberElement
                  id="cardNumber"
                  options={stripeElementOptions}
                  onChange={handleCardNumberChange}
                />
              </div>
              {fieldErrors.cardNumber && (
                <span className="field-error">{fieldErrors.cardNumber}</span>
              )}
            </div>

            {/* Cardholder Name */}
            <div className="form-field">
              <label htmlFor="cardholderName">
                Cardholder Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="cardholderName"
                className={`form-input ${fieldErrors.cardholderName ? 'error' : ''}`}
                placeholder="John Doe"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                disabled={processing}
              />
              {fieldErrors.cardholderName && (
                <span className="field-error">{fieldErrors.cardholderName}</span>
              )}
            </div>

            {/* Expiry and CVV */}
            <div className="form-row-2col">
              <div className="form-field">
                <label htmlFor="cardExpiry">
                  Exp. Month <span className="required">*</span>
                </label>
                <div
                  className={`stripe-element-wrapper ${fieldErrors.cardExpiry ? 'error' : ''}`}
                >
                  <CardExpiryElement
                    id="cardExpiry"
                    options={{ style: stripeElementStyle }}
                    onChange={handleCardExpiryChange}
                  />
                </div>
                {fieldErrors.cardExpiry && (
                  <span className="field-error">{fieldErrors.cardExpiry}</span>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="cardCvc">
                  CVV <span className="required">*</span>
                </label>
                <div
                  className={`stripe-element-wrapper ${fieldErrors.cardCvc ? 'error' : ''}`}
                >
                  <CardCvcElement
                    id="cardCvc"
                    options={{ style: stripeElementStyle }}
                    onChange={handleCardCvcChange}
                  />
                </div>
                {fieldErrors.cardCvc && (
                  <span className="field-error">{fieldErrors.cardCvc}</span>
                )}
              </div>
            </div>
          </div>

          {/* Billing Address Section */}
          <div className="form-section">
            <div className="section-header">
              <IconMapPin size={18} />
              <h4>Billing Address</h4>
            </div>

            {/* Street Address */}
            <div className="form-field">
              <label htmlFor="streetAddress">
                Street Address <span className="required">*</span>
              </label>
              <input
                type="text"
                id="streetAddress"
                className={`form-input ${fieldErrors.streetAddress ? 'error' : ''}`}
                placeholder="123 Main Street"
                value={streetAddress}
                onChange={(e) => setStreetAddress(e.target.value)}
                disabled={processing}
              />
              {fieldErrors.streetAddress && (
                <span className="field-error">{fieldErrors.streetAddress}</span>
              )}
            </div>

            {/* City and State */}
            <div className="form-row-2col">
              <div className="form-field">
                <label htmlFor="city">
                  City <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  className={`form-input ${fieldErrors.city ? 'error' : ''}`}
                  placeholder="New York"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={processing}
                />
                {fieldErrors.city && (
                  <span className="field-error">{fieldErrors.city}</span>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="state">
                  State <span className="required">*</span>
                </label>
                <select
                  id="state"
                  className={`form-select ${fieldErrors.state ? 'error' : ''}`}
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  disabled={processing}
                >
                  {US_STATES.map((st) => (
                    <option key={st.value} value={st.value}>
                      {st.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.state && (
                  <span className="field-error">{fieldErrors.state}</span>
                )}
              </div>
            </div>

            {/* ZIP Code */}
            <div className="form-field">
              <label htmlFor="zipCode">
                ZIP Code <span className="required">*</span>
              </label>
              <input
                type="text"
                id="zipCode"
                className={`form-input ${fieldErrors.zipCode ? 'error' : ''}`}
                placeholder="10001"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                disabled={processing}
                maxLength={10}
              />
              {fieldErrors.zipCode && (
                <span className="field-error">{fieldErrors.zipCode}</span>
              )}
            </div>
          </div>

          {/* Set as Default */}
          <div className="default-payment-card">
            <div className="checkbox-wrapper">
              <input
                type="checkbox"
                id="setAsDefault"
                checked={setAsDefault}
                onChange={(e) => setSetAsDefault(e.target.checked)}
                disabled={processing}
              />
              <label htmlFor="setAsDefault">
                <h4>Set as default payment method</h4>
                <p>This card will be used for all future payments</p>
              </label>
            </div>
          </div>

          {/* Notice */}
          <div className="payment-notice">
            <p>
              Changes will take effect immediately. Your next billing cycle will
              use this payment method.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-alert">
              <p>{error}</p>
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer className="update-payment-modal-footer">
        <button
          type="button"
          className="btn-cancel"
          onClick={onClose}
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="payment-form"
          className="btn-primary-update"
          disabled={processing || !stripe || !elements}
        >
          {processing ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Processing...
            </>
          ) : (
            <>
              <IconLock size={16} className="me-2" />
              Add Payment Method
            </>
          )}
        </button>
      </Modal.Footer>
    </>
  );
};

UpdatePaymentMethodModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stripeCustomerId: PropTypes.string,
  onPaymentMethodChanged: PropTypes.func.isRequired,
  organizationData: PropTypes.object,
};

export default UpdatePaymentMethodModal;
